<template>
  <div class="acm-overlay" role="dialog" aria-modal="true" aria-labelledby="acm-title" @click.self="close">
    <div class="acm-card" tabindex="-1">
      <button type="button" class="acm-close" aria-label="Close" @click="close">&times;</button>
      <h2 id="acm-title" class="acm-title">Add a payment method</h2>
      <p class="acm-sub">Your card is saved securely with Stripe. We'll charge you only when your free trial ends — no earlier.</p>

      <div v-if="!ready && !error" class="acm-spinner-wrap">
        <div class="acm-spinner" aria-hidden="true" />
        <span>Preparing secure form…</span>
      </div>

      <!-- Pre-mount errors (backend unreachable, missing pub key, etc.)
           used to only render inside the .acm-form block, which was
           hidden behind v-show="ready" — so if the mount failed the
           modal spun forever with no visible reason. Render here too. -->
      <div v-if="!ready && error" class="acm-error" role="alert" aria-live="assertive">
        {{ error }}
      </div>

      <div v-show="ready" class="acm-form">
        <label class="acm-label" for="acm-card">Card details</label>
        <div id="acm-card" ref="cardMount" class="acm-card-mount"></div>
        <div v-if="error" class="acm-error" role="alert" aria-live="assertive">{{ error }}</div>
        <button
          type="button"
          class="acm-submit"
          :disabled="!ready || saving"
          @click="submit"
        >
          <span v-if="saving">Saving…</span>
          <span v-else>Save card</span>
        </button>
        <p class="acm-meta">🔒 Powered by Stripe · PCI-DSS compliant</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api';

let stripeScriptPromise = null;
function loadStripeJs() {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (stripeScriptPromise) return stripeScriptPromise;
  stripeScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.async = true;
    s.onload = () => resolve(window.Stripe);
    s.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(s);
  });
  return stripeScriptPromise;
}

export default {
  name: 'AddCardModal',
  data() {
    return {
      ready: false,
      saving: false,
      error: '',
      clientSecret: '',
      publishableKey: '',
      stripe: null,
      elements: null,
      cardElement: null,
      escBound: null,
    };
  },
  async mounted() {
    this.escBound = (e) => { if (e.key === 'Escape') this.close(); };
    window.addEventListener('keydown', this.escBound);

    try {
      const token = localStorage.getItem('hot-token');
      const { data } = await axios.post(
        `${API_BASE}/new-checkout/billing/attach-card`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!data?.client_secret) {
        this.error = data?.error || 'Could not prepare the card form.';
        return;
      }
      this.clientSecret = data.client_secret;
      // Prefer the backend-supplied publishable key. Fall back to the
      // compile-time VUE_APP_STRIPE_PUBLIC_KEY so the modal still works
      // in envs where STRIPE_PUBLISHABLE_KEY isn't set on the backend
      // (the paid /pricing flow already uses this same frontend env
      // directly on the <stripe-element-card> component). Without the
      // fallback, a missing backend key left the spinner stuck forever.
      this.publishableKey = data.publishable_key || process.env.VUE_APP_STRIPE_PUBLIC_KEY || '';
      if (!this.publishableKey) {
        this.error = 'Stripe publishable key not configured. Contact support.';
        return;
      }

      const Stripe = await loadStripeJs();
      this.stripe = Stripe(this.publishableKey);
      this.elements = this.stripe.elements();
      this.cardElement = this.elements.create('card', {
        style: {
          base: {
            fontSize: '15px',
            color: '#1a1a1a',
            '::placeholder': { color: '#888' },
          },
          invalid: { color: '#d93025' },
        },
      });
      this.$nextTick(() => {
        this.cardElement.mount(this.$refs.cardMount);
        this.ready = true;
      });
    } catch (e) {
      this.error = e.response?.data?.error || 'Could not contact the billing server.';
    }
  },
  beforeDestroy() {
    if (this.escBound) window.removeEventListener('keydown', this.escBound);
    if (this.cardElement) { try { this.cardElement.destroy(); } catch {} }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    async submit() {
      if (!this.ready || this.saving) return;
      this.saving = true;
      this.error = '';
      try {
        const result = await this.stripe.confirmCardSetup(this.clientSecret, {
          payment_method: { card: this.cardElement },
        });
        if (result.error) {
          this.error = result.error.message || 'Card was declined.';
          return;
        }
        const pmId = result.setupIntent?.payment_method;
        if (!pmId) {
          this.error = 'Stripe did not return a payment method id.';
          return;
        }
        const token = localStorage.getItem('hot-token');
        const { data } = await axios.post(
          `${API_BASE}/new-checkout/billing/confirm-card`,
          { payment_method_id: pmId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        this.$emit('saved', data);
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not save the card.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.acm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1090;
  background: rgba(10, 10, 10, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: Inter, system-ui, sans-serif;
}
.acm-card {
  background: #fff;
  color: #1a1a1a;
  max-width: 460px;
  width: 100%;
  border-radius: 16px;
  padding: 28px;
  position: relative;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
}
.acm-close {
  position: absolute;
  top: 10px;
  right: 14px;
  background: transparent;
  border: 0;
  font-size: 28px;
  line-height: 1;
  color: #888;
  cursor: pointer;
}
.acm-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 6px;
}
.acm-sub {
  font-size: 13.5px;
  color: #555;
  margin: 0 0 20px;
  line-height: 1.5;
}
.acm-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}
.acm-card-mount {
  padding: 13px 14px;
  border: 1.5px solid #e2e2e2;
  border-radius: 10px;
  background: #fafafa;
}
.acm-error {
  margin-top: 10px;
  background: #fff4f2;
  border-left: 3px solid #d93025;
  color: #c0392b;
  padding: 10px 12px;
  font-size: 13px;
  border-radius: 6px;
}
.acm-submit {
  appearance: none;
  border: 0;
  background: #fe672c;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  padding: 13px;
  border-radius: 10px;
  width: 100%;
  margin-top: 18px;
  cursor: pointer;
}
.acm-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.acm-submit:hover:not(:disabled) { background: #e55617; }
.acm-meta {
  font-size: 11.5px;
  color: #888;
  text-align: center;
  margin: 10px 0 0;
}
.acm-spinner-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 30px 0;
  font-size: 14px;
  color: #666;
}
.acm-spinner {
  width: 22px;
  height: 22px;
  border: 3px solid #eee;
  border-top-color: #fe672c;
  border-radius: 50%;
  animation: acm-spin 0.8s linear infinite;
}
@keyframes acm-spin { to { transform: rotate(360deg); } }
</style>
